import { useState } from "react";
import { Check } from "lucide-react";
import { EhrChip } from "@/components/chat/SidebarChat/DocLink/EhrChip";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import type { WidgetField, WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { ehrUrlFor, isEhrRegistryName } from "./ehrUrl";
import {
  evidenceValueGroups,
  isSameEvidenceValue,
  type EvidenceDoc,
  type EvidenceRegistry,
  type EvidenceValueGroup,
} from "./evidenceGroups";
import { IconButton } from "./FieldRowControls";
import { localizeEnumValue, type getEnumRule } from "./getEnumRule";
import { ReasoningInfoPopover } from "./SourceGroupChip";

type MachineSource = Exclude<WidgetFieldSource, { kind: "user" }>;

function EvidenceDocRow(props: { doc: EvidenceDoc; onDocPreview: (filename: string) => void }) {
  const { doc, onDocPreview } = props;
  const reasoning = doc.reasoning?.trim();
  return (
    <div className="flex w-full items-center gap-1">
      <button
        type="button"
        onClick={() => onDocPreview(doc.filename)}
        title={doc.filename}
        className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent/60"
      >
        <FileTypeIcon filename={doc.filename} className="h-6 w-6" />
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {doc.filename}
        </span>
      </button>
      {reasoning ? <ReasoningInfoPopover reasoning={reasoning} /> : null}
    </div>
  );
}

function EvidenceRegistryRow(props: { registry: EvidenceRegistry; registryCode: string | null }) {
  const { registry, registryCode } = props;
  const reasoning = registry.reasoning?.trim();
  return (
    <div className="flex w-full items-center gap-1 px-2 py-1.5">
      {/* Only Ehitisregister gets the deep link; another registry named in the
          free-text source system is plain text, or the row would credit EHR for
          a value it never reported. */}
      {isEhrRegistryName(registry.sourceSystem) ? (
        <EhrChip url={ehrUrlFor(registryCode)} className="h-6" />
      ) : (
        <span className="min-w-0 truncate text-xs font-medium text-foreground">
          {registry.sourceSystem}
        </span>
      )}
      {reasoning ? <ReasoningInfoPopover reasoning={reasoning} /> : null}
    </div>
  );
}

/**
 * One reported value with every source that backs it. The value heads the
 * block so the reader groups by answer rather than by document, and the ✓
 * confirms that value outright — the same one-click commit the row's conflict
 * resolver offers.
 *
 * Only a value the user actually settled on is labelled as the current one, and
 * it carries no ✓ — there is no change to make. A pending field has settled
 * nothing: its value is just the first finding, so it is offered for
 * confirmation like every other candidate.
 */
function EvidenceValueBlock(props: {
  group: EvidenceValueGroup;
  /**
   * `group.value` is the canonical English string the sources reported and the
   * one a ✓ writes back; this is the same value in the reader's language.
   */
  displayValue: string;
  unit: string | null;
  isCurrent: boolean;
  canConfirm: boolean;
  /** False when the block stands alone: the Value field above already says it. */
  showValue: boolean;
  registryCode: string | null;
  tw: BuildingDataWidgetSections;
  onDocPreview: (filename: string) => void;
  onConfirmValue: (value: string) => void;
}) {
  const {
    group,
    displayValue,
    unit,
    isCurrent,
    canConfirm,
    showValue,
    registryCode,
    tw,
    onDocPreview,
    onConfirmValue,
  } = props;
  return (
    <div className="mt-3 first:mt-2">
      {showValue ? (
        <div className="flex items-center gap-2 px-2">
          <span className="min-w-0 flex-1 break-words text-sm font-medium text-foreground">
            {displayValue}
            {unit ? <span className="ml-1 text-xs text-muted-foreground">{unit}</span> : null}
          </span>
          {isCurrent ? (
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {tw.currentValueLabel}
            </span>
          ) : canConfirm ? (
            <IconButton
              onClick={() => onConfirmValue(group.value)}
              title={tw.confirmThisValue}
              color="var(--bd-ok)"
            >
              <Check size={14} />
            </IconButton>
          ) : null}
        </div>
      ) : null}
      <div className="mt-0.5">
        {group.docs.map((doc) => (
          <EvidenceDocRow key={doc.filename} doc={doc} onDocPreview={onDocPreview} />
        ))}
        {group.registries.map((registry) => (
          <EvidenceRegistryRow
            key={registry.sourceSystem}
            registry={registry}
            registryCode={registryCode}
          />
        ))}
      </div>
    </div>
  );
}

interface EvidencePanelProps {
  field: WidgetField;
  sources: MachineSource[];
  /** Resolves the sources' canonical enum values to the reader's language. */
  enumRule: ReturnType<typeof getEnumRule>;
  registryCode: string | null;
  canManage: boolean;
  tw: BuildingDataWidgetSections;
  onDocPreview: (filename: string) => void;
  onConfirmValue: (value: string) => void;
}

/**
 * Every value the machine sources reported, grouped by the value itself, with
 * all of its documents and registries listed under it — no click needed to see
 * that three files agree on one answer.
 *
 * A confirmed field has been answered, so its alternatives fold behind the
 * toggle; a pending one is still an open question, so every candidate is
 * offered at once. Read straight off `field.sources` rather than the evidence
 * query: the rationale is already on each source, so the section settles on the
 * first render instead of flickering through a fetch.
 */
export function EvidencePanel(props: EvidencePanelProps) {
  const { field, sources, tw } = props;
  const [showOthers, setShowOthers] = useState(false);
  // A rejected field keeps the machine sources it was rejected over, so without
  // this the panel lists every one of them under a SOURCE EVIDENCE heading —
  // provenance for a value the user threw away, with nothing saying so.
  if (field.state === "empty") return null;
  const groups = evidenceValueGroups(sources);
  if (groups.length === 0) return null;
  const currentIndex = groups.findIndex((group) => isSameEvidenceValue(group.value, field.value));
  const others = groups.filter((_, index) => index !== currentIndex);
  // A confirmed field folds its alternatives away even when the user's own
  // answer matches no source at all: those findings back the value they
  // replaced, and the row credits none of them (`visibleTooltipSources`,
  // WHA-3641). Listing them open under SOURCE EVIDENCE, with no current value
  // above them, reads as provenance for the saved value. Left as a count the
  // reader can open, the heading plus "Other mentions (n)" says the true thing:
  // nothing backs this value, n other values were reported.
  const collapseOthers = field.state === "confirmed";
  const listOthers = !collapseOthers || showOthers;
  return (
    <section className="py-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {tw.sourceEvidence}
        </h3>
        {collapseOthers && others.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowOthers((current) => !current)}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {tw.otherMentions.replace("{count}", String(others.length))}
          </button>
        ) : null}
      </div>
      <EvidenceValueList
        {...props}
        groups={groups}
        currentIndex={currentIndex}
        listOthers={listOthers}
      />
    </section>
  );
}

/** The value blocks under the heading — the current one, and the others when listed. */
function EvidenceValueList(
  props: EvidencePanelProps & {
    groups: ReturnType<typeof evidenceValueGroups>;
    currentIndex: number;
    listOthers: boolean;
  }
) {
  const { groups, currentIndex, listOthers, field, enumRule, registryCode, canManage, tw } = props;
  return (
    <>
      {groups.map((group, index) => {
        const isFieldValue = index === currentIndex;
        if (!isFieldValue && !listOthers) return null;
        return (
          <EvidenceValueBlock
            key={group.value}
            group={group}
            displayValue={localizeEnumValue(group.value, enumRule)}
            unit={field.unit}
            isCurrent={isFieldValue && field.state === "confirmed"}
            canConfirm={canManage}
            // Alone under the heading, the value would only repeat the Value
            // field directly above it. It comes back the moment an alternative
            // is listed, because then the documents have to say which value
            // they back.
            showValue={listOthers || !isFieldValue}
            registryCode={registryCode}
            tw={tw}
            onDocPreview={props.onDocPreview}
            onConfirmValue={props.onConfirmValue}
          />
        );
      })}
    </>
  );
}
